import { useDealContext } from '@/contexts/deal-provider';
import type { Deal } from '@/models/registry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { debounce } from 'lodash';
import { useCallback, useEffect, useState } from 'react';
import InlineTextInput from '../../ui/inline-text-input';

import ConfirmationDot from '@/components/ui/verification/confirmation-dot.tsx';
import { useDealByShortId } from '@/hooks/use-deals-selectors';
import { pdkAxios } from '@/lib/pdk-axios';
import { dealStore } from '@/models/stores';
import { ConfirmationField, ConfirmationTargetType } from '@/models/types.ts';
import { observable } from 'mobx';
import { observer } from 'mobx-react-lite';

const CoRegistrantPhoneEditor = observer(() => {
  const { currentShortId } = useDealContext();
  const deal = useDealByShortId(currentShortId);
  const dealId = deal?.id;
  const dealShortId = deal?.short_id;
  const coRegistrant = deal?.co_registrant;
  const phone = coRegistrant?.mobile_number || '';
  const coRegistrantId = coRegistrant?.id;
  const [phoneValue, setPhoneValue] = useState(phone);

  const queryClient = useQueryClient();

  const updateCoRegistrantPhoneMutation = useMutation({
    onMutate: async ({ coRegistrantPhone }) => {
      if (!dealId) return;

      dealStore.updateCoRegistrantPhone({
        id: dealId,
        phone: coRegistrantPhone,
      });
    },
    mutationFn: async ({
      coRegistrantPhone,
    }: {
      coRegistrantPhone: NonNullable<Deal['co_registrant']>['mobile_number'];
    }) => {
      if (!coRegistrantPhone) return;

      const response = await pdkAxios
        .post(
          `/deals/${dealId}/co-registrant/${coRegistrantId}/mobile-number`,
          {
            mobile_number: coRegistrantPhone,
          },
        )
        .catch((e) => {
          console.error('Error updating co-registrant phone', e);
          throw e;
        });

      return response.data;
    },

    onSuccess: () => {},
    onError: (error, variables) => {
      console.error('Error updating co-registrant phone', error, variables);
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['deal', dealShortId] });
    },
  });

  const handleCoRegistrantPhoneChange = useCallback(
    debounce((newCoRegistrantPhone: string) => {
      updateCoRegistrantPhoneMutation.mutate({
        coRegistrantPhone: newCoRegistrantPhone,
      });
    }, 1000),
    [],
  );

  useEffect(() => {
    if (phoneValue === phone) return;

    handleCoRegistrantPhoneChange(phoneValue);
  }, [phoneValue]);

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setPhoneValue(e.target.value);
  }, []);

  useEffect(() => {
    setPhoneValue(phone);
  }, [phone]);

  return (
    <div className="flex flex-row">
      <InlineTextInput
        key={`co-registrant-phone-editor-${dealId}`}
        placeholder="Co-Registrant Phone"
        value={phoneValue}
        size="sm"
        className="w-full!"
        wrapperClassName="w-full!"
        onChange={onChange}
      />
      {dealId && (
        <ConfirmationDot
          targetId={dealId}
          targetType={ConfirmationTargetType.Deal}
          field={ConfirmationField.CoRegistrantPhoneNumber}
          value={observable.box(phoneValue)}
        />
      )}
    </div>
  );
});

export default CoRegistrantPhoneEditor;
